/**
 * Núcleo de la telemetría 3.1: arma cada evento con su sobre, lleva el estado
 * del recorrido (lead, paso, pregunta en curso, tiempo activo) y se lo pasa al
 * transporte. No depende de React: el `TrackingProvider` le da el lead, el paso
 * y el estado, instala las escuchas del navegador, y cualquier pantalla (o
 * servicio) llama a `track(...)` o a los atajos de `index.ts`.
 *
 * Reglas:
 * - El tracking nunca rompe la app: todo va en try/catch.
 * - El payload se filtra contra el contrato (`filterPayload`): nunca sale una
 *   clave que no sea de su evento ni un valor que no cumpla su tipo, y por
 *   tanto nunca una respuesta, un valor de campo ni un texto de error.
 * - Desde `lead_identified`, todos los eventos llevan `lead_ulid`.
 */
import { ActivityClock } from "./activity";
import {
  AppErrorKind,
  ConsentState,
  CtaId,
  DeviceType,
  EventName,
  EventPayloads,
  ExitDestination,
  filterPayload,
  HelpChannel,
  isSlug,
  LeadVia,
  LoginFailureReason,
  LoginMethod,
  Market,
  NavigationType,
  PAYMENT_METHODS,
  PaymentMethod,
  PaymentStage,
  SCHEMA_VERSION,
  Stack,
  SubmissionErrorType,
  ThreeDsResult,
  TrackedEvent,
  TrackingEnv,
  ValidationErrorType,
  WEB_VITAL_NAMES,
  WebVitalName,
} from "./events";
import { redactPath } from "./redact";
import { compareSteps, StepId } from "./steps";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface TransportLike {
  enqueue: (event: TrackedEvent) => void;
  flush: () => unknown;
  flushOnExit: () => void;
  resume: () => void;
}

export interface IdentityLike {
  getVisitorId: () => string;
  getSessionId: () => string;
  touchSession: () => void;
  /** true si `sessionId` lo ha creado esta carga de página (no venía de cookie ni de la cola del consentimiento). */
  isNewSession: (sessionId: string) => boolean;
  /** true si el visitor_id ya existía antes de esta carga de página. */
  isReturningVisitor: () => boolean;
  setUserId?: (leadId: string) => void;
}

/** Contexto de entrada de `session_started` (lo que no sabe el tracker). */
export type EntryContext = Omit<EventPayloads["session_started"], "returning_visitor">;

export interface TrackerDeps {
  transport: TransportLike;
  identity: IdentityLike;
  env: TrackingEnv;
  market: Market | null;
  appVersion: string | null;
  getAbVariant: () => string | null;
  getPath: () => string;
  getDeviceType: () => DeviceType;
  getEntryContext: () => EntryContext;
  /** Consentimiento de analítica de CookieYes en este momento. */
  getConsent?: () => ConsentState | null;
  /** Tipo de navegación de esta carga (`performance.getEntriesByType("navigation")`). */
  getNavigationType?: () => NavigationType | null;
  uuid: () => string;
  now?: () => number;
  /** sessionStorage: paso actual, pasos completados y último método de pago de esta pestaña. */
  session?: StorageLike | null;
  /** localStorage: qué sesión y lead ya tienen `lead_identified` (vale para todas las pestañas). */
  local?: StorageLike | null;
  /** Id de esta carga de página; por defecto, un uuid nuevo. */
  pageId?: string;
}

export interface TrackOptions {
  /** El usuario se va: enviar ya con `sendBeacon`. */
  exit?: boolean;
}

export const LEAD_IDENTIFIED_KEY = "__l5_trk_lead";
export const CURRENT_STEP_KEY = "__l5_trk_step";
export const COMPLETED_STEPS_KEY = "__l5_trk_done";
export const PAYMENT_METHOD_KEY = "__l5_trk_pm";
/** Clave de la v3 (session_start por pestaña), retirada: se borra al arrancar. */
export const LEGACY_SESSION_STARTED_KEY = "__l5_trk_session";

/** Una pista de `expectIdentification` vale este tiempo. */
export const IDENTIFICATION_HINT_TTL_MS = 2 * 60_000;
/** `redirecting()` cuenta si la página se va antes de este tiempo. */
export const REDIRECT_TTL_MS = 60_000;
/** Llamada lenta al backend (`api_slow`). */
export const API_SLOW_MS = 3_000;
/** Máximo de `app_error` por carga de página. */
export const MAX_APP_ERRORS = 5;

const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

/** El lead solo se acepta si es un ULID (26 caracteres), como pide el contrato. */
export const normalizeLeadUlid = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return ULID.test(trimmed) ? trimmed : null;
};

/** `REACT_APP_ENVIROMENT` (con su errata) → `env` del contrato. */
export const envFromEnviroment = (value: string | undefined): TrackingEnv => {
  const v = (value || "").toUpperCase();
  if (v === "PRD" || v === "PROD" || v === "PRODUCTION") return "prd";
  if (v === "TEST" || v === "STG" || v === "STAGING" || v === "PRE") return "stg";
  return "dev";
};

interface StripeLikeError {
  type: string;
  code?: string;
  decline_code?: string;
}

const isStripeError = (error: unknown): error is StripeLikeError =>
  !!error &&
  typeof error === "object" &&
  typeof (error as { type?: unknown }).type === "string" &&
  /_error$/.test((error as { type: string }).type);

/** Código de rechazo de Stripe (o el código de error del servidor). */
export const declineCodeOf = (error: unknown): string | null => {
  if (!error || typeof error !== "object") return null;
  const e = error as Record<string, unknown>;
  const code = e.decline_code ?? e.code ?? e.error ?? e.status;
  return code === undefined || code === null || code === "" ? null : String(code);
};

/**
 * Tipo de un error al enviar un paso:
 * - `stripe`: lo rechaza Stripe (code = decline_code o code de Stripe);
 * - `conflict`: HTTP 409;
 * - `status`: cualquier otra respuesta HTTP de error (code = el status);
 * - `network`: no hubo respuesta (fetch falló, sin conexión…).
 * `signature` y `proposal` no se deducen: los pone quien llama.
 */
export const classifySubmissionError = (
  error: unknown
): { error_type: SubmissionErrorType; code: string | null } => {
  if (isStripeError(error)) {
    return { error_type: "stripe", code: error.decline_code || error.code || error.type };
  }
  const status = statusOf(error);
  if (status !== null) {
    return { error_type: status === "409" ? "conflict" : "status", code: status };
  }
  return { error_type: "network", code: null };
};

const statusOf = (error: unknown): string | null => {
  const status =
    error && typeof error === "object" ? (error as { status?: unknown }).status : undefined;
  return status === undefined || status === null || status === "" ? null : String(status);
};

/**
 * Motivo de un acceso fallido: 401 (o la respuesta «RQ not valid» del monolito)
 * → `invalid`; sin respuesta → `network`; cualquier otra cosa → `unknown`.
 */
export const loginFailureReasonOf = (error: unknown): LoginFailureReason => {
  if (error === "RQ not valid") return "invalid";
  const status = statusOf(error);
  const code =
    error && typeof error === "object" ? (error as { code?: unknown }).code : undefined;
  if (status === "401" || String(code) === "401") return "invalid";
  if (status === null && (error instanceof TypeError || isNetworkError(error))) return "network";
  return "unknown";
};

const isNetworkError = (error: unknown): boolean =>
  !!error &&
  typeof error === "object" &&
  /failed to fetch|networkerror|network request failed|load failed/i.test(
    String((error as { message?: unknown }).message || "")
  );

const isPaymentMethod = (value: unknown): value is PaymentMethod =>
  typeof value === "string" && (PAYMENT_METHODS as readonly string[]).indexOf(value) >= 0;

interface ActiveQuestion {
  id: string;
  stack: Stack;
  viewedAt: number;
  attempts: number;
}

interface SavedStep {
  sid: string;
  step: StepId;
  raw: string | null;
  at: number;
  /** ms activos en el paso hasta que se guardó (recarga, vuelta del banco). */
  active?: number;
}

interface CompletedSteps {
  sid: string;
  steps: string[];
}

interface IdentifiedLeads {
  sid: string;
  leads: string[];
}

export interface FieldCompletion {
  field: string;
  time_ms: number;
  edits: number;
  corrections: number;
}

export class Tracker {
  private readonly deps: TrackerDeps;
  private readonly now: () => number;
  private readonly pageId: string;
  private readonly clock = new ActivityClock();

  private seq = 0;
  private lead: string | null = null;
  private step: StepId | null = null;
  private rawStatus: string | null = null;
  private stepEnteredAt: number;
  /** Total del reloj de actividad que corresponde al inicio del paso actual. */
  private stepActiveFrom = 0;
  private lastHeartbeatActive = 0;
  private startedSession: string | null = null;
  private identified: IdentifiedLeads | null = null;
  private identifyCalled = false;
  private identificationHint: { via: LeadVia; at: number } | null = null;
  private pageLoadedSent = false;
  private redirect: { destination: ExitDestination; at: number } | null = null;
  private lastField: string | null = null;
  private question: ActiveQuestion | null = null;
  private validationAttempts: Record<string, number> = {};
  private exitIntentStep: StepId | null = null;
  private lastSelectedMethod: PaymentMethod | null = null;
  private appErrors = 0;
  private vitalsSent: Record<string, true> = {};

  constructor(deps: TrackerDeps) {
    this.deps = deps;
    this.now = deps.now ?? (() => Date.now());
    this.pageId = deps.pageId ?? deps.uuid();
    this.stepEnteredAt = this.now();
  }

  // ── Envío ──────────────────────────────────────────────────────────────

  track<K extends EventName>(name: K, payload: EventPayloads[K], opts?: TrackOptions): void {
    try {
      const sid = this.ensureSession();
      this.emit(name, payload, sid);
      if (opts?.exit) this.deps.transport.flushOnExit();
    } catch {
      /* el tracking nunca rompe la app */
    }
  }

  /** Arranque: reintenta la cola guardada de visitas anteriores. */
  start(): void {
    try {
      this.remove(LEGACY_SESSION_STARTED_KEY);
      this.deps.transport.resume();
    } catch {
      /* noop */
    }
  }

  /** La pestaña se oculta o la página se va: lo pendiente sale ya. */
  flushOnExit(): void {
    try {
      this.deps.transport.flushOnExit();
    } catch {
      /* noop */
    }
  }

  private emit<K extends EventName>(name: K, payload: EventPayloads[K], sid: string): void {
    this.seq += 1;
    const event: TrackedEvent<K> = {
      client_event_id: this.deps.uuid(),
      event_name: name,
      timestamp: new Date(this.now()).toISOString(),
      visitor_id: this.deps.identity.getVisitorId(),
      session_id: sid,
      lead_ulid: this.lead,
      env: this.deps.env,
      market: this.deps.market,
      device_type: this.deps.getDeviceType(),
      app_version: this.deps.appVersion ? this.deps.appVersion.slice(0, 64) : null,
      ab_variant: this.deps.getAbVariant()?.slice(0, 128) || null,
      path: redactPath(this.deps.getPath()),
      payload: filterPayload(name, payload),
      schema: SCHEMA_VERSION,
      page_id: this.pageId,
      seq: this.seq,
      consent: this.consent(),
    };
    this.deps.transport.enqueue(event as TrackedEvent);
  }

  private consent(): ConsentState | null {
    try {
      return this.deps.getConsent?.() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Devuelve la sesión actual. `session_started` sale solo si el session_id lo
   * acaba de crear esta página (una vez por SESIÓN, no por pestaña: una pestaña
   * nueva o una recarga reutilizan el de la cookie). Si ya se conoce el lead,
   * la sesión nueva se une a él con `lead_identified`.
   */
  private ensureSession(): string {
    this.deps.identity.touchSession();
    const sid = this.deps.identity.getSessionId();
    if (sid === this.startedSession) return sid;
    const previous = this.startedSession;
    this.startedSession = sid;
    if (this.deps.identity.isNewSession(sid)) {
      this.deps.identity.getVisitorId();
      this.emit(
        "session_started",
        {
          ...this.deps.getEntryContext(),
          returning_visitor: this.deps.identity.isReturningVisitor(),
        },
        sid
      );
    }
    // Una sesión que caduca con la página abierta: su lead ya estaba ahí.
    if (this.lead && previous !== null) this.markIdentified(sid, "resumed");
    return sid;
  }

  // ── Lead ───────────────────────────────────────────────────────────────

  /**
   * La pantalla que está a punto de cambiar el lead dice cómo lo conoce
   * (`created`, `login`, `link`, `partner`). Vale para el próximo lead nuevo
   * durante 2 minutos.
   */
  expectIdentification(via: LeadVia): void {
    this.identificationHint = { via, at: this.now() };
  }

  /**
   * El lead ya existe (calculadora, login, enlace, recuperación de sesión…).
   * Emite `lead_identified {via}` una vez por sesión y lead; desde ese momento
   * todos los eventos llevan `lead_ulid`. Sin lead (o con uno que no es un
   * ULID), los eventos siguientes van con `lead_ulid: null`.
   *
   * `via`: la pista de `expectIdentification` si la hay; si no, `resumed` para
   * el lead que ya estaba al cargar y `unknown` para el resto.
   */
  identifyLead(leadId: unknown): void {
    try {
      const atLoad = !this.identifyCalled;
      this.identifyCalled = true;
      const id = normalizeLeadUlid(leadId);
      const changed = id !== this.lead;
      this.lead = id;
      if (!id) return;
      const via = changed ? this.takeHint() ?? (atLoad ? "resumed" : "unknown") : "resumed";
      this.deps.identity.setUserId?.(id);
      this.markIdentified(this.ensureSession(), via);
    } catch {
      /* noop */
    }
  }

  leadUlid(): string | null {
    return this.lead;
  }

  private takeHint(): LeadVia | null {
    const hint = this.identificationHint;
    this.identificationHint = null;
    if (!hint || this.now() - hint.at > IDENTIFICATION_HINT_TTL_MS) return null;
    return hint.via;
  }

  /** Leads ya identificados en la sesión `sid` (memoria, localStorage o sessionStorage). */
  private identifiedLeads(sid: string): string[] {
    const sources = [
      this.identified,
      this.readJson<IdentifiedLeads>(LEAD_IDENTIFIED_KEY, this.deps.local),
      this.readJson<IdentifiedLeads>(LEAD_IDENTIFIED_KEY),
    ];
    return sources.reduce<string[]>((acc, entry) => {
      if (!entry || entry.sid !== sid || !Array.isArray(entry.leads)) return acc;
      return acc.concat(entry.leads.filter((lead) => acc.indexOf(lead) < 0));
    }, []);
  }

  private markIdentified(sid: string, via: LeadVia): void {
    const lead = this.lead;
    if (!lead) return;
    const leads = this.identifiedLeads(sid);
    if (leads.indexOf(lead) >= 0) return;
    this.identified = { sid, leads: leads.concat(lead) };
    const value = JSON.stringify(this.identified);
    this.write(LEAD_IDENTIFIED_KEY, value, this.deps.local);
    this.write(LEAD_IDENTIFIED_KEY, value);
    this.emit("lead_identified", { via }, sid);
  }

  // ── Acceso ─────────────────────────────────────────────────────────────

  loginCodeRequested(): void {
    this.track("login_code_requested", {});
  }

  loginSucceeded(method: LoginMethod): void {
    this.track("login_succeeded", { method });
  }

  loginFailed(method: LoginMethod, error: unknown): void {
    this.track("login_failed", { method, reason: loginFailureReasonOf(error) });
  }

  // ── Pasos ──────────────────────────────────────────────────────────────

  /**
   * El cliente está en `step` (sacado de la ruta) con el lead en `rawStatus`.
   * Al cambiar de paso: `step_completed` del anterior si avanza, `step_back` si
   * vuelve atrás, y `step_viewed` del nuevo. Una recarga en el mismo paso y la
   * misma sesión no cuenta como paso nuevo (se conserva desde cuándo está y el
   * tiempo activo que llevaba). El primer paso conocido de la página emite
   * `page_loaded`.
   */
  setStep(step: StepId, rawStatus: string | null | undefined): void {
    try {
      const raw = rawStatus || null;
      const now = this.now();
      if (this.step === null && this.restoreStep(step, raw)) {
        this.pageLoaded();
        return;
      }
      if (this.step === step) {
        if (this.rawStatus === null && raw) {
          this.rawStatus = raw;
          this.saveStep();
        }
        return;
      }
      const prev = this.step;
      const prevRaw = this.rawStatus;
      const prevEnteredAt = this.stepEnteredAt;
      const prevActive = this.activeOnStepMs(now);

      this.step = step;
      this.rawStatus = raw;
      this.stepEnteredAt = now;
      this.stepActiveFrom = this.clock.totalAt(now);
      this.question = null;
      this.validationAttempts = {};
      this.exitIntentStep = null;
      this.lastSelectedMethod = null;

      if (prev === null) this.pageLoaded();
      if (prev !== null) {
        const direction = compareSteps(prev, step);
        if (direction > 0) {
          this.track("step_completed", {
            step: prev,
            raw_status: prevRaw,
            time_on_step_ms: Math.max(0, now - prevEnteredAt),
            active_on_step_ms: prevActive,
            repeat: this.markCompleted(prev),
          });
        } else if (direction < 0) {
          this.track("step_back", { from: prev, to: step });
        }
      }
      this.track("step_viewed", { step, raw_status: raw });
      this.saveStep();
    } catch {
      /* noop */
    }
  }

  currentStep(): StepId {
    return this.step ?? "other";
  }

  timeOnStepMs(): number {
    return Math.max(0, this.now() - this.stepEnteredAt);
  }

  activeOnStepMs(now: number = this.now()): number {
    return Math.max(0, Math.round(this.clock.totalAt(now) - this.stepActiveFrom));
  }

  private restoreStep(step: StepId, raw: string | null): boolean {
    const saved = this.readJson<SavedStep>(CURRENT_STEP_KEY);
    if (!saved || saved.step !== step) return false;
    if (saved.sid !== this.deps.identity.getSessionId()) return false;
    const now = this.now();
    this.step = step;
    this.rawStatus = saved.raw ?? raw;
    this.stepEnteredAt = typeof saved.at === "number" ? saved.at : now;
    const active = typeof saved.active === "number" && saved.active > 0 ? saved.active : 0;
    this.stepActiveFrom = this.clock.totalAt(now) - active;
    return true;
  }

  private saveStep(): void {
    if (this.step === null) return;
    const saved: SavedStep = {
      sid: this.deps.identity.getSessionId(),
      step: this.step,
      raw: this.rawStatus,
      at: this.stepEnteredAt,
      active: this.activeOnStepMs(),
    };
    this.write(CURRENT_STEP_KEY, JSON.stringify(saved));
  }

  /** Apunta el paso como completado en la sesión; true si ya lo estaba (volvió atrás y avanzó otra vez). */
  private markCompleted(step: StepId): boolean {
    const sid = this.deps.identity.getSessionId();
    const saved = this.readJson<CompletedSteps>(COMPLETED_STEPS_KEY);
    const steps = saved && saved.sid === sid && Array.isArray(saved.steps) ? saved.steps : [];
    if (steps.indexOf(step) >= 0) return true;
    this.write(COMPLETED_STEPS_KEY, JSON.stringify({ sid, steps: steps.concat(step) }));
    return false;
  }

  // ── Sesión y página ────────────────────────────────────────────────────

  /** Una vez por carga de página, cuando se conoce el paso. */
  private pageLoaded(): void {
    if (this.pageLoadedSent) return;
    this.pageLoadedSent = true;
    let navigation: NavigationType | null = null;
    try {
      navigation = this.deps.getNavigationType?.() ?? null;
    } catch {
      navigation = null;
    }
    this.track("page_loaded", { step: this.currentStep(), navigation });
  }

  /** La página vuelve de la caché de atrás/adelante (`pageshow` con `persisted`). */
  pageRestored(): void {
    this.redirect = null;
    this.track("page_loaded", { step: this.currentStep(), navigation: "back_forward" });
  }

  /** Interacción con la pestaña visible (ratón, teclado, toque, scroll). */
  interaction(): void {
    try {
      this.clock.interact(this.now());
    } catch {
      /* noop */
    }
  }

  /** Latido: `active_ms` = tiempo con interacción desde el latido anterior. */
  heartbeat(): void {
    const total = this.clock.totalAt(this.now());
    const active = Math.max(0, Math.round(total - this.lastHeartbeatActive));
    this.lastHeartbeatActive = total;
    this.track("heartbeat", { step: this.currentStep(), active_ms: active });
  }

  tabHidden(): void {
    this.clock.stop(this.now());
    this.track("tab_hidden", { step: this.currentStep() });
    this.flushOnExit();
  }

  tabVisible(): void {
    this.track("tab_visible", { step: this.currentStep() });
  }

  /** Como mucho una vez por paso. */
  exitIntent(): void {
    const step = this.currentStep();
    if (this.exitIntentStep === step) return;
    this.exitIntentStep = step;
    this.track("exit_intent_detected", { step });
  }

  /**
   * La app va a mandar al cliente fuera de la página (banco del 3DS, web…):
   * el `page_exited` que venga después dirá `reason: redirect` y adónde. Lo
   * pendiente sale ya, por si la navegación corta el envío.
   */
  redirecting(destination: ExitDestination): void {
    try {
      this.redirect = { destination, at: this.now() };
      this.saveStep();
      this.flushOnExit();
    } catch {
      /* noop */
    }
  }

  /**
   * `pagehide`: la página se va (cerrar, recargar, salir o la redirección de
   * `redirecting`). No es el fin de la sesión: eso lo decide el servidor por
   * silencio.
   */
  pageExited(): void {
    try {
      const now = this.now();
      const redirect =
        this.redirect && now - this.redirect.at <= REDIRECT_TTL_MS ? this.redirect : null;
      this.redirect = null;
      const active = this.activeOnStepMs(now);
      this.clock.stop(now);
      this.saveStep();
      this.track(
        "page_exited",
        {
          step: this.currentStep(),
          reason: redirect ? "redirect" : "unload",
          destination: redirect ? redirect.destination : null,
          time_on_step_ms: this.timeOnStepMs(),
          active_on_step_ms: active,
          last_field: this.lastField,
        },
        { exit: true }
      );
    } catch {
      /* noop */
    }
  }

  // ── Formularios ────────────────────────────────────────────────────────

  /** Foco en un campo (solo el nombre): queda como `last_field` de `page_exited`. */
  fieldFocused(field: string): void {
    this.lastField = field;
  }

  /** Sale de un campo en el que ha escrito (blur). Sin el valor, nunca. */
  fieldCompleted(completion: FieldCompletion): void {
    this.lastField = completion.field;
    this.track("field_completed", { ...completion, step: this.currentStep() });
  }

  /**
   * Un campo pasa a tener error (o cambia de error); `attempt` cuenta por campo
   * dentro del paso. `code`: motivo corto si se sabe (p. ej. el code de Stripe),
   * nunca el texto.
   */
  fieldInvalid(
    field: string,
    errorType: ValidationErrorType = "format",
    code: string | null = null
  ): void {
    const step = this.currentStep();
    const key = `${step}|${field}`;
    const attempt = (this.validationAttempts[key] || 0) + 1;
    this.validationAttempts[key] = attempt;
    if (this.question) this.question.attempts += 1;
    this.track("field_invalid", {
      field,
      step,
      error_type: errorType,
      code: isSlug(code) ? code : null,
      attempt,
    });
  }

  /** Falla el envío del paso (no un campo concreto). Un fallo de pago va como `paymentFailed`. */
  stepSubmitFailed(
    error: unknown,
    override?: { error_type?: SubmissionErrorType; code?: string | number | null }
  ): void {
    const classified = classifySubmissionError(error);
    const code = override && override.code !== undefined ? override.code : classified.code;
    this.track("step_submit_failed", {
      step: this.currentStep(),
      error_type: override?.error_type ?? classified.error_type,
      code: code === null || code === undefined ? null : String(code),
    });
  }

  // ── Preguntas ──────────────────────────────────────────────────────────

  questionViewed(questionId: string | number, stack: Stack): void {
    const id = String(questionId);
    if (this.question && this.question.id === id && this.question.stack === stack) return;
    this.question = { id, stack, viewedAt: this.now(), attempts: 1 };
    this.track("question_viewed", { question_id: id, stack });
  }

  /**
   * Pregunta contestada (sin la respuesta, nunca). `attempts` = 1 + errores de
   * validación vistos en esa pregunta antes de contestarla.
   */
  questionAnswered(questionId: string | number, stack: Stack): void {
    const id = String(questionId);
    const q =
      this.question && this.question.id === id && this.question.stack === stack
        ? this.question
        : null;
    this.question = null;
    this.track("question_answered", {
      question_id: id,
      stack,
      time_ms: q ? Math.max(0, this.now() - q.viewedAt) : 0,
      attempts: q ? q.attempts : 1,
    });
  }

  // ── Precio y contenidos ────────────────────────────────────────────────

  /** Abre un contenido marcado (`data-track`): IPID, condiciones, coberturas… */
  contentOpened(contentId: string): void {
    if (!isSlug(contentId)) return;
    this.track("content_opened", { content_id: contentId, step: this.currentStep() });
  }

  // ── Pago ───────────────────────────────────────────────────────────────

  /** Elige un método en el selector. No repite el mismo método dentro del mismo paso. */
  paymentMethodSelected(method: PaymentMethod): void {
    if (this.lastSelectedMethod === method) return;
    this.lastSelectedMethod = method;
    this.track("payment_method_selected", { method });
  }

  /** Envía el método (guardar la tarjeta o pagar con wallet). */
  paymentMethodSubmitted(method: PaymentMethod): void {
    this.track("payment_method_submitted", { method });
  }

  /** Stripe y el backend aceptan el método. Se recuerda para el cobro de validación. */
  paymentMethodSaved(method: PaymentMethod): void {
    this.write(PAYMENT_METHOD_KEY, method);
    this.track("payment_method_saved", { method });
  }

  /**
   * Falla el pago: al guardar el método (`setup`) o en el cobro de validación
   * (`ensure_funds`). Sin método, el último guardado en esta pestaña.
   */
  paymentFailed(stage: PaymentStage, error: unknown, method?: PaymentMethod | null): void {
    const saved = this.read(PAYMENT_METHOD_KEY);
    const resolved = method ?? (isPaymentMethod(saved) ? saved : null);
    this.track("payment_failed", { stage, method: resolved, decline_code: declineCodeOf(error) });
  }

  threeDsRequested(stage: PaymentStage): void {
    this.track("threeds_requested", { stage });
  }

  threeDsCompleted(stage: PaymentStage, result: ThreeDsResult): void {
    this.track("threeds_completed", { stage, result });
  }

  // ── Ayuda ──────────────────────────────────────────────────────────────

  ctaClicked(ctaId: CtaId): void {
    this.track("cta_clicked", { cta_id: ctaId, step: this.currentStep() });
  }

  helpRequested(channel: HelpChannel): void {
    this.track("help_requested", { step: this.currentStep(), channel });
  }

  // ── Técnico ────────────────────────────────────────────────────────────

  /** Error de JavaScript sin capturar (como mucho 5 por página). `name`: clase del error, nunca el mensaje. */
  appError(kind: AppErrorKind, name: string | null): void {
    if (this.appErrors >= MAX_APP_ERRORS) return;
    this.appErrors += 1;
    this.track("app_error", { kind, name, step: this.currentStep() });
  }

  /** Duración de una llamada al backend: `api_slow` si pasa de 3 s. */
  apiTiming(endpoint: string, ms: number): void {
    if (!(ms > API_SLOW_MS)) return;
    this.track("api_slow", { endpoint, ms, step: this.currentStep() });
  }

  /** Métrica de velocidad de la página: una por métrica y carga. */
  webVital(name: string, value: number): void {
    if ((WEB_VITAL_NAMES as readonly string[]).indexOf(name) < 0) return;
    if (this.vitalsSent[name]) return;
    this.vitalsSent[name] = true;
    this.track("web_vital", {
      name: name as WebVitalName,
      value: Math.max(0, value),
      step: this.currentStep(),
    });
  }

  // ── Almacenamiento ─────────────────────────────────────────────────────

  private read(key: string, store: StorageLike | null | undefined = this.deps.session): string | null {
    try {
      return store?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private readJson<T>(
    key: string,
    store: StorageLike | null | undefined = this.deps.session
  ): T | null {
    const raw = this.read(key, store);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private write(
    key: string,
    value: string,
    store: StorageLike | null | undefined = this.deps.session
  ): void {
    try {
      store?.setItem(key, value);
    } catch {
      /* sin almacenamiento: se repetirá algún evento, no rompe nada */
    }
  }

  private remove(key: string): void {
    try {
      this.deps.session?.removeItem(key);
    } catch {
      /* noop */
    }
  }
}
